import { useId } from 'react';
import {
  DropDown,
  FlexColumn,
  FormControls,
  TextField,
} from '@elementary/common-ui/components';
import { Tooltip, Typography } from '@mui/material';
import { Controller, get, UseFormReturn } from 'react-hook-form';
import { AddTestForm } from 'shared/types';

type Props = {
  methods: UseFormReturn<AddTestForm, any, undefined>;
  title?: string;
  prefix: string;
  countName:
    | 'detectionPeriod'
    | 'trainingPeriodCount'
    | 'anomalyConfig.trainingPeriod.count';
  unitName:
    | 'detectionUnit'
    | 'trainingPeriodUnit'
    | 'anomalyConfig.trainingPeriod.period';
  unitOptions: { value: string; label: string }[];
  validate?: (values: AddTestForm) => string | boolean | undefined;
  countAriaLabel?: string;
  unitAriaLabel?: string;
  disabledReason?: string;
};

const PeriodCountField = ({
  methods,
  title,
  prefix,
  countName,
  unitName,
  unitOptions,
  validate,
  countAriaLabel,
  unitAriaLabel,
  disabledReason,
}: Props) => {
  const errorId = useId();
  const error = get(methods.formState.errors, countName);
  const errorMessage =
    typeof error?.message === 'string' && error.message
      ? error.message
      : undefined;

  return (
    <FlexColumn sx={{ gap: 1 }}>
      <Tooltip title={disabledReason ?? ''}>
        <FormControls.Row sx={{ flexWrap: 'wrap', gap: 1 }}>
          {title && <FormControls.FormText>{title}</FormControls.FormText>}
          <Typography component="span">{prefix}</Typography>
          <TextField
            type="number"
            sx={{ width: '64px' }}
            error={!!error}
            disabled={!!disabledReason}
            inputProps={{
              'aria-label': countAriaLabel,
              'aria-describedby': errorMessage ? errorId : undefined,
              min: 1,
              step: 1,
            }}
            {...methods.register(countName, {
              valueAsNumber: true,
              validate: validate
                ? () => validate(methods.getValues())
                : undefined,
            })}
          />
          <Controller
            name={unitName}
            control={methods.control}
            render={({ field }) => (
              <DropDown
                aria-label={unitAriaLabel}
                size="large"
                sortOptions={false}
                options={unitOptions}
                sx={{ minWidth: '120px' }}
                {...field}
                disabled={!!disabledReason}
                onChange={(value) => {
                  field.onChange(value);
                  if (validate) methods.trigger(countName);
                }}
              />
            )}
          />
        </FormControls.Row>
      </Tooltip>
      {errorMessage && (
        <Typography id={errorId} variant="caption" color="error" role="alert">
          {errorMessage}
        </Typography>
      )}
    </FlexColumn>
  );
};

export default PeriodCountField;
